import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { FeedbackRow, FeedbackStatus } from "@/hooks/use-feedback-admin";
import { FeedbackAttachments } from "./FeedbackAttachments";
import {
  FEEDBACK_PRIORITIES,
  FEEDBACK_PRIORITY_BADGE,
  isFeedbackPriority,
} from "@/lib/feedback-priority";

// acknowledged=Løst, closed=Avsluttet (verdiene er uendret under panseret).
const STATUS_LABEL: Record<FeedbackStatus, string> = {
  new: "Ny",
  acknowledged: "Løst",
  closed: "Avsluttet",
  deleted: "Slettet",
};

const STATUS_COLOR: Record<FeedbackStatus, string> = {
  new: "bg-amber-100 text-amber-800 border-amber-200",
  acknowledged: "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed: "bg-slate-100 text-slate-700 border-slate-200",
  deleted: "bg-muted text-muted-foreground",
};

const SELECTABLE: FeedbackStatus[] = ["new", "acknowledged", "closed"];

interface Props {
  item: FeedbackRow;
  pending: boolean;
  /** Statusbytte (+ valgfri melding til melder på e-post). */
  onSubmit: (status: FeedbackStatus, message: string) => void;
  onDelete: () => void;
}

/**
 * Ett feedback-kort. Statusbytte i nedtrekket åpner en meldingsboks: skriver du
 * en melding sendes status + melding til melder på e-post (VIB-1239), lar du den
 * stå tom oppdateres bare statusen.
 */
export function FeedbackCard({ item, pending, onSubmit, onDelete }: Props) {
  const [pendingStatus, setPendingStatus] = useState<FeedbackStatus | null>(null);
  const [message, setMessage] = useState("");

  const boxOpen = pendingStatus !== null;

  const submit = () => {
    if (!pendingStatus) return;
    onSubmit(pendingStatus, message.trim());
    setPendingStatus(null);
    setMessage("");
  };
  const cancel = () => {
    setPendingStatus(null);
    setMessage("");
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <SrIcon
            name={item.type === "bug" ? "alert-triangle" : "sparkles"}
            className={cn(
              "w-5 h-5 mt-0.5 shrink-0",
              item.type === "bug" ? "text-destructive" : "text-primary",
            )}
          />
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className={STATUS_COLOR[item.status]}>
                {STATUS_LABEL[item.status]}
              </Badge>
              {/*
                VIB-2085: prioritet ved siden av status. Uten den må Erik lese
                hele meldingen for å vite om noen står fast.
              */}
              {isFeedbackPriority(item.priority) ? (
                <Badge
                  variant="secondary"
                  className={FEEDBACK_PRIORITY_BADGE[item.priority]}
                >
                  {FEEDBACK_PRIORITIES[item.priority].label}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">
                  Ukjent prioritet
                </Badge>
              )}
              <span className="text-sm font-medium">
                {item.user?.full_name ?? "Ukjent bruker"}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatDate(item.created_at)}
              </span>
              {item.page_url && (
                <a
                  href={item.page_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-0.5"
                >
                  <SrIcon name="external-link" className="w-3 h-3" />
                  {item.page_url}
                </a>
              )}
            </div>
            <p className="text-sm whitespace-pre-wrap break-words">{item.body}</p>

            <FeedbackAttachments paths={item.attachment_paths} />

            {item.replies.length > 0 && (
              <div className="space-y-1">
                {item.replies.map((r) => (
                  <div key={r.id} className="text-xs bg-muted/50 rounded px-2 py-1.5">
                    <span className="font-medium">{r.author?.full_name ?? "Admin"}:</span>{" "}
                    <span className="whitespace-pre-wrap break-words">{r.body}</span>
                    <span className="text-muted-foreground ml-1">· {formatDate(r.created_at)}</span>
                  </div>
                ))}
              </div>
            )}

            {boxOpen && (
              <div className="space-y-2 rounded-md border bg-muted/30 p-2">
                <p className="text-xs text-muted-foreground">
                  Status settes til <strong>{STATUS_LABEL[pendingStatus]}</strong>. Skriv en
                  melding (valgfri) som sendes til melder på e-post.
                </p>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Melding til melder (valgfri, sendes på e-post)…"
                  rows={2}
                  className="text-sm min-h-0"
                  autoFocus
                />
                <div className="flex items-center gap-2">
                  <Button size="sm" className="h-8" disabled={pending} onClick={submit}>
                    {pending ? (
                      <SrIcon name="loader2" className="w-4 h-4 animate-spin" />
                    ) : message.trim() ? (
                      "Send & oppdater"
                    ) : (
                      "Oppdater status"
                    )}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8" onClick={cancel}>
                    Avbryt
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Select
              value={pendingStatus ?? item.status}
              onValueChange={(s) => {
                setPendingStatus(s as FeedbackStatus);
                setMessage("");
              }}
            >
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SELECTABLE.map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="sm"
              disabled
              title="Funksjonell etter VIB-679"
              className="h-8 text-xs"
            >
              Lag Linear-issue
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm("Slett denne tilbakemeldingen?")) onDelete();
              }}
              className="h-8 w-8 p-0"
              title="Slett (myk)"
              aria-label="Slett tilbakemelding"
            >
              <SrIcon name="trash" className="w-4 h-4 text-destructive" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
